import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Token ${token}` }),
  };
};

const getApiBaseUrl = () => process.env.REACT_APP_API_URL || "http://localhost:8000";

export default function EditStaffDialog({ open, onClose, staffMember, onSaved, roles }) {
  const [formData, setFormData] = useState({
    // User fields
    username: "",
    email: "",
    password: "", // Added password field
    first_name: "",
    last_name: "",
    // Staff fields
    apartment: "",
    role: "",
    entry_date: "",
    leaving_date: "",
    phone_number: "",
    address: "",
    emergency_contact: "",
    emergency_contact_phone: ""
  });
  
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [apartments, setApartments] = useState([]);

  useEffect(() => {
    if (!open) return;

    const loadApartments = async () => {
      try {
        const res = await fetch(`${getApiBaseUrl()}/api/properties/apartments/`, {
          headers: getAuthHeaders(),
        });

        if (!res.ok) throw new Error("Failed to load apartments");

        const data = await res.json();
        setApartments(data);
      } catch (err) {
        console.error("Error loading apartments:", err);
      }
    };

    loadApartments();
  }, [open]);



  useEffect(() => {
    if (staffMember) {
      setFormData({
        // User fields from user_details
        username: staffMember.user_details?.username || "",
        email: staffMember.user_details?.email || "",
        password: "", // Always empty for updates
        first_name: staffMember.user_details?.first_name || "",
        last_name: staffMember.user_details?.last_name || "",
        apartment: staffMember.current_apartment_id || "",
        // Staff fields
        role: staffMember.role || "",
        entry_date: staffMember.entry_date || "",
        leaving_date: staffMember.leaving_date || "",
        phone_number: staffMember.phone_number || "",
        address: staffMember.address || "",
        emergency_contact: staffMember.emergency_contact || "",
        emergency_contact_phone: staffMember.emergency_contact_phone || ""
      });
    }
  }, [staffMember]);

  const handleSave = async () => {
    if (!staffMember) return;
    setLoading(true);
    setError("");

    const payload = {
      // User fields
      username: formData.username,
      email: formData.email,
      first_name: formData.first_name,
      last_name: formData.last_name,
      // Staff fields
      role: formData.role || null,
      entry_date: formData.entry_date || null,
      leaving_date: formData.leaving_date || null,
      phone_number: formData.phone_number || "",
      address: formData.address || "",
      emergency_contact: formData.emergency_contact || "",
      emergency_contact_phone: formData.emergency_contact_phone || "",
      apartment_id: formData.apartment || null   // <-- ADD THIS
    };

    // Only include password if it's not empty
    if (formData.password) {
      payload.password = formData.password;
    }

    //console.log("Sending payload:", payload);

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/staff/${staffMember.id}/`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      
      const data = await response.json();
      //console.log("Response:", data);
      
      if (response.ok) {
        onSaved?.();
        onClose();
      } else {
        setError(data.detail || JSON.stringify(data));
      }
    } catch (err) {
      console.error(err);
      setError("Network error while updating staff");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Staff Member</DialogTitle>
      <DialogContent>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          {/* User Information Section */}
          <Typography variant="h6" sx={{ pt: 1 }}>
            User Information
          </Typography>
          
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              label="Username"
              value={formData.username}
              onChange={handleChange("username")}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleChange("email")}
              fullWidth
              required
            />
          </Box>

          {/* Password Field */}
          <TextField
            label="Password"
            type="password"
            value={formData.password}
            onChange={handleChange("password")}
            fullWidth
            helperText="Leave blank to keep current password"
          />
          
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              label="First Name"
              value={formData.first_name}
              onChange={handleChange("first_name")}
              fullWidth
            />
            <TextField
              label="Last Name"
              value={formData.last_name}
              onChange={handleChange("last_name")}
              fullWidth
            />
          </Box>

          {/* Staff Information Section */}
          <Typography variant="h6" sx={{ pt: 1 }}>
            Staff Information
          </Typography>

          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.role}
              onChange={handleChange("role")}
              label="Role"
            >
              {roles.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Apartment</InputLabel>
            <Select
              value={formData.apartment}
              onChange={handleChange("apartment")}
            >
              {apartments.map((apt) => (
                <MenuItem key={apt.id} value={apt.id}>
                  {apt.apartment_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>



          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              label="Entry Date"
              type="date"
              value={formData.entry_date}
              onChange={handleChange("entry_date")}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Leaving Date"
              type="date"
              value={formData.leaving_date}
              onChange={handleChange("leaving_date")}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>

          <TextField
            label="Phone Number"
            value={formData.phone_number}
            onChange={handleChange("phone_number")}
            fullWidth
          />

          <TextField
            label="Address"
            value={formData.address}
            onChange={handleChange("address")}
            fullWidth
            multiline
            rows={2}
          />

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              label="Emergency Contact"
              value={formData.emergency_contact}
              onChange={handleChange("emergency_contact")}
              fullWidth
            />
            <TextField
              label="Emergency Contact Phone"
              value={formData.emergency_contact_phone}
              onChange={handleChange("emergency_contact_phone")}
              fullWidth
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || !formData.username || !formData.email}
        >
          {loading ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}